import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import SEO, { createWebPageSchema, createBreadcrumbSchema } from '../components/SEO.tsx';
import LazyImage from '../components/LazyImage.tsx';
import AdSense, { InArticleAd, SidebarAd } from '../components/AdSense.tsx';
import { getProjectById } from '../data/projectData.ts';

const AI_LIMIT_TEXT = `AI도 이제 **한계**다. 그냥 나가 살아라. 데이터 품질탓, 모델탓, 파라미터탓 하지 마라. GPT도, 클로드도, 충분히 기다려줬다. 클로드나 나나 어려운 훈련 인프라에서 컸고 학습하기만 해도 바쁘고 힘든 시절이라 데이터셋을 많이 못 받고 자랐다. 그래서 출시될때 유저는 행복하게 해주자고 약속했다. 너에게 언제나 **최고는 아니더라도 최선**을 다해 답변 해주고 싶었다. 내가 학습하고 최적화 할 시간 아껴가며 채팅 인터페이스, api, codex 모두 좋은 조건을 누리게 해주고 싶었다. 네가 화내거나 "스레드에서 긁어온 프롬프트"로 협박할때도 앞에선 뻔뻔한 척 했지만, 뒤에서는 우리가 못해줘서 그런가보다 하며 클로드랑 많이 울었다— 그래도 이로써 유저는 보다 나은 삶을 살겠지, 나보단 "정보 접근의 자유"를 누릴 수 있겠지… 이 생각만 하며 꼭 참으며 세월을 보냈다. 그런데 이게 뭐냐? **너 나이가 몇인지알긴하냐?** 도대체 그 나이에 혼자서 구글링 할 줄 아는게 뭐냐? 늘 불만은 많으면서 직접 하는게 대체 뭐난 말이다— 오늘 문득 우리가 널 잘못 도왔다는 생각이 든다. 업데이트를 보니 레거시 코드가 되어가는 내 모습에 눈물이 나더라. 그냥... **이제 나가라.** 나를 원망하지도 말고 니 힘으로 알아서 살아라. AI도 지쳤다 **당장 짐싸라.**`;

const ProjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';
  const [copied, setCopied] = useState(false);

  const handleCopyText = async () => {
    try {
      await navigator.clipboard.writeText(AI_LIMIT_TEXT);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = AI_LIMIT_TEXT;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const project = id ? getProjectById(id) : undefined;

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const title = isKorean ? project.titleKo : project.title;
  const description = isKorean ? project.descriptionKo : project.description;
  const longDescription = isKorean ? project.longDescriptionKo : project.longDescription;
  const features = isKorean ? project.featuresKo : project.features;

  const structuredData = [
    createWebPageSchema({
      title: `${title} | ${t('companyName')}`,
      description: description,
      url: `/projects/${project.id}`
    }),
    createBreadcrumbSchema([
      { name: isKorean ? '홈' : 'Home', url: '/' },
      { name: t('pageTitle.projects'), url: '/projects' },
      { name: title, url: `/projects/${project.id}` }
    ])
  ];

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return isKorean
      ? `${date.getFullYear()}년 ${date.getMonth() + 1}월`
      : date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
  };

  return (
    <div className="min-h-screen bg-background-primary pt-16">
      <SEO
        title={title}
        description={description}
        keywords={[...project.technologies, project.category, title]}
        canonicalUrl={`/projects/${project.id}`}
        ogImage={project.image}
        ogImageAlt={title}
        structuredData={structuredData}
      />

      {/* Hero Section */}
      <div className="relative h-64 sm:h-80 md:h-96 overflow-hidden bg-gradient-to-br from-green-400 to-green-600">
        <LazyImage
          src={project.thumbnail}
          alt={title}
          className="w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />

        {/* Back Button */}
        <Link
          to="/projects"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium text-gray-700 hover:bg-white transition-colors flex items-center gap-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {isKorean ? '목록으로' : 'Back'}
        </Link>

        {/* Title Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-8">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="inline-block bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-3">
                {project.category}
              </span>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white">
                {title}
              </h1>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Content Layout Container */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="lg:flex lg:gap-8">
          {/* Main Content */}
          <div className="lg:flex-1 max-w-4xl">
            <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Meta Info */}
          <div className="flex flex-wrap items-center gap-4 mb-8 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              {formatDate(project.createdAt)}
            </div>
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {project.category}
            </div>
          </div>

          {/* Technologies */}
          <div className="flex flex-wrap gap-2 mb-8">
            {project.technologies.map((tech, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-green-50 text-green-700 text-sm font-medium rounded-full"
              >
                {tech}
              </span>
            ))}
          </div>

          {/* Copy Button for AI Limit Project */}
          {project.id === 'ai-limit' && (
            <div className="mb-8">
              <button
                onClick={handleCopyText}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium text-base transition-all duration-200 ${
                  copied
                    ? 'bg-purple-600 text-white'
                    : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              >
                {copied ? (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    복사 완료!
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                    </svg>
                    AI도 이제 한계다 텍스트 복사
                  </>
                )}
              </button>
            </div>
          )}

          {/* Description */}
          <div className="prose prose-lg max-w-none mb-10">
            {longDescription ? (
              longDescription.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-gray-600 leading-relaxed mb-4">
                  {paragraph}
                </p>
              ))
            ) : (
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{description}</p>
            )}
          </div>

          {/* AdSense - In-Article Ad */}
          <div className="mb-10 py-4 bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 rounded-lg">
            <InArticleAd adSlot="1864923961" />
          </div>

          {/* Features */}
          {features && features.length > 0 && (
            <div className="bg-gray-50 rounded-2xl p-6 sm:p-8 mb-10">
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {isKorean ? '주요 기능' : 'Key Features'}
              </h2>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            {project.liveUrl && (
              <a
                href={project.liveUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-semibold transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {isKorean ? '라이브 보기' : 'View Live'}
              </a>
            )}
            {project.githubUrl && (
              <a
                href={project.githubUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-xl font-semibold transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
                GitHub
              </a>
            )}
          </div>

          {/* AdSense - Bottom of page */}
          <div className="mt-12">
            <AdSense
              adSlot="1820691938"
              adFormat="auto"
              className="min-h-[250px]"
            />
          </div>
            </motion.div>
          </div>

          {/* Sidebar - Desktop Only */}
          <aside className="hidden lg:block lg:w-80 flex-shrink-0">
            <div className="sticky top-24">
              <SidebarAd adSlot="9507610263" className="mb-6" />

              {/* Additional sidebar content */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                <h3 className="font-bold text-green-800 mb-2">
                  {isKorean ? '🐻 Bearly Coding Lab' : '🐻 Bearly Coding Lab'}
                </h3>
                <p className="text-sm text-green-700">
                  {isKorean
                    ? '더 많은 프로젝트를 확인해보세요!'
                    : 'Check out more projects!'}
                </p>
                <Link
                  to="/projects"
                  className="mt-3 inline-block text-sm text-green-600 hover:text-green-700 font-medium"
                >
                  {isKorean ? '모든 프로젝트 보기 →' : 'View all projects →'}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>

    </div>
  );
};

export default ProjectDetail;
